import React, { useState, useEffect } from 'react';
import {
  Users, Mic, BookOpen, Award, TrendingUp, Star, UserCheck
} from 'lucide-react';
import adminApi from '../../../api/adminApi';
import '../../../assets/css/admin/dashboard.css';

const Dashboard = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    adminApi
      .get('/dashboard')
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error('Dashboard fetch error:', err);
        setError('Could not load dashboard data.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="admin-container"><p style={{ padding: 40 }}>Loading dashboard...</p></div>;
  }

  if (error || !data) {
    return <div className="admin-container"><p style={{ padding: 40 }}>{error || 'No data available.'}</p></div>;
  }

  const { stats, counts, growth, recent_patients, recent_appointments, top_therapists } = data;

  const statCards = [
    { title: 'Total Patients', value: stats.total_patients, change: 'This Month', icon: Users, color: '#001F3F', emoji: '👨‍⚕️', trend: 'up' },
    { title: 'Active Sessions', value: stats.active_sessions_today, change: 'Today', icon: Mic, color: '#ff7a00', emoji: '🎙️', trend: 'up' },
    { title: 'Success Rate', value: `${stats.success_rate}%`, change: 'Completed Sessions', icon: Award, color: '#ff7a00', emoji: '🏆', trend: 'up' },
  ];

  const additionalStats = [
    { title: 'Total Patients', value: counts.total_patients, growth: growth.total_patients, icon: 'P', color: '#FF6B35', bgColor: '#FFF5F1' },
    { title: 'Total Therapists', value: counts.total_therapists, growth: growth.total_therapists, icon: 'T', color: '#001F3F', bgColor: '#E8F4F8' },
    { title: 'Active Appointments', value: counts.active_appointments, growth: growth.active_appointments, icon: 'A', color: '#FF6B35', bgColor: '#FFF5F1' },
    { title: 'Completed Sessions', value: counts.completed_sessions, growth: growth.completed_sessions, icon: 'S', color: '#001F3F', bgColor: '#E8F4F8' },
  ];

  return (
    <div className="admin-container">
      {/* Welcome Header */}
      <div className="admin-welcome fade-in">
        <div className="admin-welcome-content">
          <h1 className="admin-welcome-title">Smart Therapy Dashboard</h1>
          <p className="admin-welcome-subtitle">Welcome back, Admin! Here's your therapy center overview.</p>
        </div>
        <div className="admin-welcome-stats">
          <div className="admin-quick-stat">
            <span className="admin-quick-number">{stats.active_sessions_today}</span>
            <span className="admin-quick-label">Today's Sessions</span>
          </div>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="admin-stats-grid fade-in-up">
        {statCards.map((stat, idx) => (
          <div key={idx} className="admin-stat-card admin-hover-lift">
            <div className="admin-stat-icon" style={{ backgroundColor: stat.color + '20' }}>
              <span className="admin-stat-emoji">{stat.emoji}</span>
            </div>
            <div className="admin-stat-content">
              <p className="admin-stat-title">{stat.title}</p>
              <h2 className="admin-stat-value">{stat.value}</h2>
              <div className="admin-stat-change">
                <TrendingUp size={16} className={`admin-trend-icon ${stat.trend}`} />
                <span>{stat.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Stats Section */}
      <div className="admin-enhanced-stats-grid">
        {additionalStats.map((stat, idx) => (
          <div key={idx} className="admin-enhanced-stat-card">
            <div className="admin-enhanced-stat-header">
              <div className="admin-enhanced-stat-icon" style={{ backgroundColor: stat.bgColor, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="admin-enhanced-stat-badge">
                <TrendingUp size={12} />
                <span>{stat.growth > 0 ? '+' : ''}{stat.growth}%</span>
              </div>
            </div>
            <div className="admin-enhanced-stat-body">
              <h3 className="admin-enhanced-stat-title">{stat.title}</h3>
              <div className="admin-enhanced-stat-value">{stat.value}</div>
              <p className="admin-enhanced-stat-subtitle">from last month</p>
            </div>
            <div className="admin-enhanced-stat-progress">
              <div
                className="admin-enhanced-stat-progress-bar"
                style={{ width: `${Math.min(Math.max(stat.growth, 0) * 5, 100)}%`, backgroundColor: stat.color }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="admin-main-grid">
        {/* Recent Patients */}
        <div className="admin-card slide-in-right">
          <div className="admin-card-header">
            <div className="admin-card-title-group">
              <h3 className="admin-card-title">Recent Patients</h3>
              <p className="admin-card-subtitle">Latest patient registrations</p>
            </div>
          </div>
          <div className="admin-patients-table">
            <div className="admin-table-header">
              <span>Patient</span>
              <span>Therapist</span>
              <span>Status</span>
            </div>
            {recent_patients.length === 0 ? (
              <p style={{ padding: 16, color: '#888' }}>No patients yet.</p>
            ) : recent_patients.map((patient, idx) => (
              <div key={idx} className="admin-table-row fade-in-row">
                <div className="admin-patient-info">
                  <div className="admin-avatar">{patient.avatar}</div>
                  <span className="admin-patient-name">{patient.name}</span>
                </div>
                <span className="admin-therapist-name">{patient.therapist}</span>
                <span className={`admin-status-badge admin-status-${patient.status.toLowerCase()}`}>
                  {patient.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Therapists */}
        <div className="admin-card slide-in-up">
          <div className="admin-card-header">
            <div className="admin-card-title-group">
              <h3 className="admin-card-title">Top Therapists</h3>
              <p className="admin-card-subtitle">Best performing doctors</p>
            </div>
          </div>
          <div className="admin-therapists-list">
            {top_therapists.length === 0 ? (
              <p style={{ padding: 16, color: '#888' }}>No therapists yet.</p>
            ) : top_therapists.map((therapist, idx) => (
              <div key={idx} className="admin-therapist-item admin-hover-lift">
                <div className="admin-therapist-avatar">{therapist.avatar}</div>
                <div className="admin-therapist-info">
                  <div className="admin-therapist-name">{therapist.name}</div>
                  <div className="admin-therapist-specialty">{therapist.specialty}</div>
                  <div className="admin-therapist-rating">
                    <Star size={14} fill="#f59e0b" color="#f59e0b" />
                    <span>{therapist.rating}</span>
                    <span className="admin-session-count">({therapist.sessions} sessions)</span>
                  </div>
                </div>
                <div className={`admin-therapist-status admin-therapist-status-${(therapist.status || '').toLowerCase().replace(' ', '-')}`}>
                  <UserCheck size={14} />
                  <span>{therapist.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Appointments Section */}
      <div className="admin-appointments-section">
        <div className="admin-appointments-header">
          <div>
            <h2>Recent Appointments</h2>
            <p className="admin-appointments-subtitle">Upcoming and pending appointments</p>
          </div>
        </div>
        <div className="admin-appointments-table-wrapper">
          <div className="admin-appointments-table">
            <div className="admin-table-header-row">
              <div className="admin-table-cell">Patient</div>
              <div className="admin-table-cell">Therapist</div>
              <div className="admin-table-cell">Date</div>
              <div className="admin-table-cell">Time</div>
              <div className="admin-table-cell">Status</div>
            </div>
            {recent_appointments.length === 0 ? (
              <p style={{ padding: 16, color: '#888' }}>No appointments yet.</p>
            ) : recent_appointments.map((appointment) => (
              <div key={appointment.id} className="admin-appointment-row">
                <div className="admin-table-cell" data-label="Patient:">{appointment.patient}</div>
                <div className="admin-table-cell" data-label="Therapist:">{appointment.therapist}</div>
                <div className="admin-table-cell" data-label="Date:">{appointment.date}</div>
                <div className="admin-table-cell" data-label="Time:">{appointment.time}</div>
                <div className="admin-table-cell" data-label="Status:">
                  <span className={`admin-status-pill ${appointment.status.toLowerCase()}`}>
                    {appointment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;